import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useContacts, useMe } from "@/api/hooks";
import { Alert, Avatar, EmptyState, Modal, PageHeader, Spinner, formatDate } from "@/components/ui";
import { can } from "@/lib/permissions";
import { ContactForm } from "./ContactForm";

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function ContactsPage() {
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim());
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const me = useMe();
  const contacts = useContacts(q);
  const rows = contacts.data?.pages.flatMap((p) => p.items) ?? [];
  const canCreate = can(me.data?.permissions ?? [], "contacts:create");

  const newButton = canCreate && <button type="button" className="btn-primary" onClick={() => setCreating(true)}>New contact</button>;

  return (
    <>
      <PageHeader title="Contacts" subtitle="Buyers, engineers and decision makers at your accounts." actions={newButton} />
      <div className="mb-4">
        <input type="search" className="input max-w-sm" placeholder="Search name, email, company or phone"
          aria-label="Search contacts" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {contacts.isError && <Alert>Could not load contacts. {contacts.error.message}</Alert>}
      {contacts.isLoading && <Spinner />}
      {contacts.isSuccess && rows.length === 0 && (
        q ? <EmptyState title="No matches" body={`No contacts match “${q}”.`} />
          : <EmptyState title="No contacts yet" body="Add your first buyer or import from a spreadsheet." action={newButton} />
      )}
      {rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Added</th></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas" onClick={() => navigate(`/contacts/${c.id}`)}>
                  <td className="px-4 py-3">
                    <Link to={`/contacts/${c.id}`} className="flex items-center gap-3 font-medium hover:text-brand" onClick={(e) => e.stopPropagation()}>
                      <Avatar name={c.full_name} />
                      <span>{c.full_name}<span className="block text-xs font-normal text-muted">{c.job_title}</span></span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">{c.company_name}</td>
                  <td className="px-4 py-3 text-muted">{c.email}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{c.phone}</td>
                  <td className="whitespace-nowrap px-4 py-3">{c.owner?.full_name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{formatDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {contacts.hasNextPage && (
        <div className="mt-4 text-center">
          <button type="button" className="btn-ghost" disabled={contacts.isFetchingNextPage} onClick={() => void contacts.fetchNextPage()}>
            {contacts.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
      {creating && (
        <Modal title="New contact" onClose={() => setCreating(false)}>
          <ContactForm onCancel={() => setCreating(false)} onSaved={(c) => { setCreating(false); navigate(`/contacts/${c.id}`); }} />
        </Modal>
      )}
    </>
  );
}
